import React, { useState, useEffect } from 'react';
import { X, Loader2, Eye, Search, User, Edit3, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const AdminCustomers = () => {
  const { t } = useLang();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetPwdId, setResetPwdId] = useState(null);
  const [resetPwdName, setResetPwdName] = useState('');
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [editCustomer, setEditCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', company: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await api('/api/customers');
      setCustomers(data);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (c) => {
    setEditCustomer(c);
    setEditForm({ name: c.name, email: c.email, phone: c.phone || '', company: c.company || '', address: c.address || '' });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api(`/api/admin/users/${editCustomer.id}`, { method: 'PUT', body: JSON.stringify(editForm) });
      toast.success('Klienti u përditësua me sukses!');
      setEditCustomer(null);
      loadCustomers();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const deleteCustomer = async (id, name) => {
    if (!window.confirm(`A jeni i sigurt që doni të fshini klientin "${name}"? Ky veprim nuk mund të kthehet.`)) return;
    try {
      await api(`/api/admin/users/${id}`, { method: 'DELETE' });
      toast.success('Klienti u fshi me sukses!');
      loadCustomers();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <LoadingSpinner />;

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search) ||
    (c.company || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-customers" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">{t('customers_title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{customers.length} {t('customers_registered')}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder={t('search_customers')}
          className="input w-full pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="customer-search-input"
        />
        <Eye className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('name')}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('phone')}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('company').replace(' (Optional)','').replace(' (Opsionale)','').replace(' (Opcional)','')}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('address')}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('date')}</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">{t('actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filtered.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-800/50 transition-colors" data-testid={`customer-row-${customer.id}`}>
                <td className="px-4 py-3 font-medium text-white">{customer.name}</td>
                <td className="px-4 py-3 text-gray-300 text-sm">{customer.email}</td>
                <td className="px-4 py-3 text-gray-300 text-sm">{customer.phone || '—'}</td>
                <td className="px-4 py-3 text-gray-300 text-sm">{customer.company || '—'}</td>
                <td className="px-4 py-3 text-gray-400 text-sm max-w-[200px] truncate">{customer.address || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {new Date(customer.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(customer)}
                      className="text-xs bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 px-2.5 py-1.5 rounded-lg font-medium transition-all"
                      data-testid={`edit-customer-${customer.id}`}
                    >
                      {t('edit')}
                    </button>
                    <button
                      onClick={() => { setResetPwdId(customer.id); setResetPwdName(customer.name); setResetPwdValue(''); }}
                      className="text-xs bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 px-2.5 py-1.5 rounded-lg font-medium transition-all"
                      data-testid={`reset-pwd-${customer.id}`}
                    >
                      {t('reset_password')}
                    </button>
                    <button
                      onClick={() => deleteCustomer(customer.id, customer.name)}
                      className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-2.5 py-1.5 rounded-lg font-medium transition-all"
                      data-testid={`delete-customer-${customer.id}`}
                    >
                      {t('delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            {search ? t('no_results') : t('no_customers')}
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editCustomer && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-lg w-full" data-testid="edit-customer-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{t('edit_customer')}</h3>
              <button onClick={() => setEditCustomer(null)} className="p-1.5 rounded-lg hover:bg-gray-800"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('full_name')}</label>
                <input type="text" className="input w-full" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-customer-name" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input type="email" className="input w-full" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-customer-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('phone')}</label>
                  <input type="tel" className="input w-full" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-customer-phone" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('company').replace(' (Optional)','').replace(' (Opsionale)','').replace(' (Opcional)','')}</label>
                  <input type="text" className="input w-full" value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} data-testid="edit-customer-company" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t('address')}</label>
                <input type="text" className="input w-full" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="edit-customer-address" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveEdit} disabled={saving} className="btn-primary flex-1 py-2.5 font-bold" data-testid="save-customer-btn">
                  {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
                  {t('save_changes')}
                </button>
                <button onClick={() => setEditCustomer(null)} className="btn-secondary flex-1 py-2.5">{t('cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwdId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full" data-testid="reset-pwd-modal">
            <h3 className="font-heading text-xl font-bold text-white mb-2">{t('change_password')}</h3>
            <p className="text-sm text-gray-400 mb-4">{t('customer')}: {resetPwdName}</p>
            <input type="text" placeholder={t('new_password')} className="input w-full mb-4" value={resetPwdValue} onChange={(e) => setResetPwdValue(e.target.value)} data-testid="reset-pwd-input" />
            <div className="flex gap-2">
              <button onClick={async () => {
                try { await api(`/api/admin/users/${resetPwdId}/password`, { method: 'PUT', body: JSON.stringify({ new_password: resetPwdValue }) }); toast.success(t('success')); setResetPwdId(null); }
                catch (err) { toast.error(err.message); }
              }} className="btn-primary flex-1" data-testid="confirm-reset-pwd">{t('save')}</button>
              <button onClick={() => setResetPwdId(null)} className="btn-secondary flex-1">{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminCustomers;
